import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Truck, ShieldCheck, Sparkles, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import PromoPopup from "@/components/PromoPopup";
import { HeroBannerSlider } from "@/components/HeroBannerSlider";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";

function CategoryCircle({ slug, name, icon }) {
  return (
    <Link
      to={`/katalog?category=${slug}`}
      data-testid={`category-${slug}`}
      className="flex flex-col items-center gap-2 min-w-[80px] group"
    >
      <div className="w-16 h-16 rounded-2xl brand-soft-bg grid place-items-center group-hover:brand-bg-primary group-hover:scale-105 transition-all duration-300">
        <span className="text-2xl">{icon || "🛍️"}</span>
      </div>
      <span className="text-xs font-semibold text-center leading-tight max-w-[80px] line-clamp-2 group-hover:brand-text-primary">
        {name}
      </span>
    </Link>
  );
}

const CAT_ICONS = {
  "ayam-kampung-omega": "🥚",
  "ayam-kampung-omega-grade-a": "🏆",
  "ayam-negeri": "🍳",
  "ayam-negeri-omega": "✨",
  "telur-bebek": "🦆",
  "telur-bebek-asin": "🧂",
  "telur-puyuh": "🐣",
};

export default function HomePage() {
  const { branding } = useBranding();
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [newest, setNewest] = useState([]);
  const [bestseller, setBestseller] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  // Tampilkan popup "after_login" hanya 1x setelah login (flag di sessionStorage)
  const [showAfterLogin, setShowAfterLogin] = useState(false);

  useEffect(() => {
    if (user && sessionStorage.getItem("show_after_login_popup") === "1") {
      setShowAfterLogin(true);
      sessionStorage.removeItem("show_after_login_popup");
    }
  }, [user]);

  useEffect(() => {
    api.get("/banners").then((r) => setBanners(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/products", { params: { featured: true, limit: 8 } }).then((r) => setFeatured(r.data.items)).catch(() => {});
    api.get("/products", { params: { sort: "newest", limit: 8 } }).then((r) => setNewest(r.data.items)).catch(() => {});
    api.get("/products", { params: { bestseller: true, sort: "bestseller", limit: 8 } }).then((r) => setBestseller(r.data.items)).catch(() => {});
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
      {/* Popup banner promo */}
      {showAfterLogin
        ? <PromoPopup location="after_login" />
        : <PromoPopup location="homepage" />
      }
      {/* Hero Section — Banner Slider Engine v1 */}
      <HeroBannerSlider banners={banners} />

      {/* Trust badges */}
      <section className="mt-6 grid grid-cols-3 gap-2 sm:gap-4">
        {[
          { icon: Truck, title: "Pengiriman Cepat", desc: "Dikirim hari yang sama" },
          { icon: ShieldCheck, title: "Kualitas Terjamin", desc: "Produk original & terkurasi" },
          { icon: Sparkles, title: "Harga Terbaik", desc: "Langsung dari toko kami" },
        ].map((b) => {
          const Icon = b.icon;
          return (
            <div key={b.title} className="bg-white border border-[#E5E5E5] rounded-2xl p-3 sm:p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl brand-soft-bg grid place-items-center shrink-0">
                <Icon className="brand-text-primary" size={18} />
              </div>
              <div className="min-w-0">
                <div className="font-bold text-xs sm:text-sm leading-tight">{b.title}</div>
                <div className="text-[10px] sm:text-xs text-[#5C5C5C] truncate">{b.desc}</div>
              </div>
            </div>
          );
        })}
      </section>

      {/* Categories */}
      <section className="mt-10" data-testid="home-categories">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest brand-text-primary font-bold">Kategori</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mt-1">Pilih Kategori Favoritmu</h2>
          </div>
        </div>
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
          {categories.map((c) => (
            <CategoryCircle key={c.id} slug={c.slug} name={c.name} icon={CAT_ICONS[c.slug]} />
          ))}
        </div>
      </section>

      {/* Product Sections */}
      {[
        { title: "Produk Unggulan", subtitle: "Pilihan terbaik untuk keluarga", items: featured, link: "/katalog?featured=true" },
        { title: "Paling Laris", subtitle: "Favorit pelanggan kami", items: bestseller, link: "/katalog?bestseller=true" },
        { title: "Produk Terbaru", subtitle: "Baru tiba di toko kami", items: newest, link: "/katalog" },
      ].map((sec) => sec.items.length > 0 && (
        <section key={sec.title} className="mt-10" data-testid={`section-${sec.title.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest brand-text-primary font-bold">{sec.subtitle}</div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold mt-1">{sec.title}</h2>
            </div>
            <Link to={sec.link} className="text-sm font-bold brand-text-primary flex items-center gap-1 hover:gap-2 transition-all">
              Lihat semua <ChevronRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {sec.items.slice(0, 4).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
